import { RotateCw, StickyNote, X } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';

import { NoticeIcon } from 'common-src/components/base';

import {
  daysUntil,
  getAppointmentReadinessStatus,
  getPatient,
  getPostVisitCardStatus,
  getPostVisitTaskDaysRolling,
  getTaskReadinessStatus,
  isTaskCanceledForReadiness,
  isTaskReadinessComplete,
} from '../../helpers';
import {
  getNoticeDetail,
  getNoticeLabel,
  getNoticeTone,
  MocaOutcomeIcon,
  useMocaPrompt,
} from '../MocaPrompt';
import {
  CardHeadline,
  ClinicLocalTime,
  clinicVisitTimes,
  FactSection,
  ReadinessSquares,
  STATUS_WORD,
  VisitCard,
  visitCode,
  visitLabelWithoutCode,
  visitOpenLabel,
  VisitSections,
} from '../Visit';
import styles from './AppointmentCard.module.scss';

const getOverallCardStatus = (tasks = []) => {
  const status = getAppointmentReadinessStatus(tasks);
  return status === 'default' ? 'neutral' : status;
};

const AppointmentCard = ({
  appt,
  onClick,
  isSelected,
  appointmentTasks = [],
  isPostVisit = false,
  onDismiss,
  departmentLabelById = {},
  huddleNoteCount = 0,
  onOpenSection,
}) => {
  const moca = useMocaPrompt(appt, 'Prepare');
  const patient = appt?.patient ?? getPatient(appt?.patientId);
  const departmentLabel = departmentLabelById[`${appt?.departmentAthenaId ?? ''}`] || '';
  const clinicDate = appt?.departmentLocalDate || appt?.date;
  const days = clinicDate ? daysUntil(clinicDate, appt?.departmentTimezone) : null;
  const hasValidDays = Number.isFinite(days);
  const activeAppointmentTasks = appointmentTasks.filter(
    (task) => !isTaskCanceledForReadiness(task),
  );
  const totalTaskCount = appointmentTasks.length;
  const completedTaskCount = appointmentTasks.filter((task) =>
    isTaskReadinessComplete(task),
  ).length;
  const overallCardStatus = isPostVisit
    ? getPostVisitCardStatus(activeAppointmentTasks)
    : getOverallCardStatus(appointmentTasks);
  const rollingTasks = activeAppointmentTasks.filter((task) => !isTaskReadinessComplete(task));
  const rollingTaskCount = rollingTasks.length;
  const oldestRollingDays = rollingTaskCount
    ? Math.max(...rollingTasks.map(getPostVisitTaskDaysRolling))
    : 0;
  const followUpTotalCount = activeAppointmentTasks.length;
  const followUpCompletedCount = followUpTotalCount - rollingTaskCount;
  const readinessBlocks = appointmentTasks.map((task) => ({
    id: task.id,
    status: getTaskReadinessStatus(task),
  }));
  const followUpBlocks = activeAppointmentTasks.map((task) => ({
    id: task.id,
    status: isTaskReadinessComplete(task) ? 'green' : overallCardStatus,
  }));
  const dateLabel = hasValidDays
    ? days === 0
      ? 'Today'
      : days === 1
        ? 'Tomorrow'
        : days < 0
          ? `${Math.abs(days)}d ago`
          : `in ${days}d`
    : '';

  const times = clinicVisitTimes(appt);
  const code = visitCode({ cptCode: appt?.cptCode, visitType: appt?.visitType });
  const visit = {
    code,
    kindName: appt?.visitType,
    label: visitLabelWithoutCode(appt?.visitType),
    range: times.range,
    localRange: times.localRange,
  };
  const statusWord = STATUS_WORD[isPostVisit ? 'followUp' : 'readiness'][overallCardStatus];
  const open = (section) => () => onOpenSection?.(section);

  return (
    <VisitCard
      status={overallCardStatus}
      selected={isSelected}
      onOpen={onClick}
      openLabel={visitOpenLabel(patient?.name, visit.label)}
    >
      <CardHeadline
        visit={visit}
        when={dateLabel}
        soon={!isPostVisit && hasValidDays && days <= 2}
        aside={
          <>
            {isPostVisit && <MocaOutcomeIcon appt={appt} onClick={open('post-visit-moca')} />}
            {!isPostVisit && appt?.lowMocaPrompt && !moca.state.hiddenInPrepare && (
              <NoticeIcon
                tone={getNoticeTone(moca.state)}
                label={getNoticeLabel(moca.state)}
                detail={getNoticeDetail(appt.latestMoca, moca.state)}
                onClick={open('readiness-moca')}
              />
            )}
            {isPostVisit && rollingTaskCount > 0 && (
              <NoticeIcon
                tone="alert"
                icon={RotateCw}
                count={rollingTaskCount}
                label={`${rollingTaskCount} rolling${oldestRollingDays > 0 ? ` · oldest ${oldestRollingDays}d` : ''}`}
                detail={rollingTasks
                  .slice(0, 3)
                  .map((task) => task.title)
                  .filter(Boolean)
                  .join(' · ')}
                onClick={open('post-visit-tasks')}
              />
            )}
            {huddleNoteCount > 0 && (
              <NoticeIcon
                tone="neutral"
                icon={StickyNote}
                count={huddleNoteCount}
                label={`${huddleNoteCount} huddle note${huddleNoteCount === 1 ? '' : 's'}`}
                onClick={open('readiness-notes')}
              />
            )}
            {isPostVisit && onDismiss && (
              <button
                type="button"
                className={styles.dismiss}
                onClick={() => onDismiss(appt?.id)}
                aria-label={`Hide ${patient?.name || 'this'} post-visit follow-up`}
                title="Hide from post-visit view"
              >
                <X size={14} aria-hidden="true" />
              </button>
            )}
          </>
        }
      />
      <VisitSections>
        <FactSection label="Member">
          <span className={styles.memberName}>{patient?.name}</span>
        </FactSection>
        {isPostVisit && (
          <FactSection label="Booking">
            <ClinicLocalTime appt={appt} clinicName={departmentLabel} />
          </FactSection>
        )}
        <FactSection label={isPostVisit ? 'Follow-up' : 'Readiness'}>
          <ReadinessSquares
            blocks={isPostVisit ? followUpBlocks : readinessBlocks}
            done={isPostVisit ? followUpCompletedCount : completedTaskCount}
            total={isPostVisit ? followUpTotalCount : totalTaskCount}
          >
            <span className={styles.srOnly}>{statusWord}</span>
          </ReadinessSquares>
        </FactSection>
      </VisitSections>
    </VisitCard>
  );
};

AppointmentCard.propTypes = {
  onOpenSection: PropTypes.func,
  appt: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    patient: PropTypes.shape({
      name: PropTypes.string,
    }),
    patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    visitType: PropTypes.string,
    cptCode: PropTypes.string,
    durationMinutes: PropTypes.number,
    date: PropTypes.string,
    time: PropTypes.string,
    departmentLocalDate: PropTypes.string,
    departmentLocalTime: PropTypes.string,
    departmentTimezone: PropTypes.string,
    departmentAthenaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    latestMoca: PropTypes.shape({ score: PropTypes.number, maxScore: PropTypes.number }),
    lowMocaPrompt: PropTypes.bool,
  }),
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
  appointmentTasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.number,
      priority: PropTypes.string,
    }),
  ),
  isPostVisit: PropTypes.bool,
  onDismiss: PropTypes.func,
  departmentLabelById: PropTypes.object,
  huddleNoteCount: PropTypes.number,
};

export default AppointmentCard;
